<template>
  <section>
    <l-map
      v-if="hasLayers"
      :style="`height: 600px; width: 100%`"
      :center="center"
      :options="leafletOptions"
      :bounds="bounds"
      :zoom="12"
      ref="serviceAreaMap">

      <l-tile-layer v-bind="basemap" />

      <l-geo-json
        v-for="(layer, index) in processedLayers"
        :key="index"
        :geojson="layer.geojson"
        :options="geoJSONOptions"
        :optionsStyle="layer.style">
      </l-geo-json>

      <l-control position="bottomright">
        <div class="map-info map-legend instructor-legend">
          <strong>Instructors</strong>
          <label
            v-for="(entry, index) in legendEntries"
            :key="index"
            class="legend-entry"
            :class="{ 'legend-entry--hidden': isHidden(entry) }">
            <input
              type="checkbox"
              :checked="!isHidden(entry)"
              @change="toggleInstructor(entry)" />
            <i class="zone-theme" :style="legendSwatchStyle(entry)"></i>
            <span>{{ entry.name }}</span>
          </label>
        </div>
      </l-control>
    </l-map>

    <div v-if="!hasLayers" class="alert alert-info text-center">
      No service area geometry data available. Zones must have sectors with geometry to display on the map.
    </div>
  </section>
</template>

<script>
  import { latLngBounds } from 'leaflet';
  import { LMap, LControl, LGeoJson, LTileLayer } from 'vue2-leaflet';
  import { BASEMAP } from '../../../../../DriveScout/Config.js';
  import ColorHash from 'color-hash';

  const colorHash = new ColorHash();
  const UNASSIGNED_COLOR = '#999999';
  const STRIPE_WIDTH = 20;

  export default {
    name: 'InstructorServiceAreaMap',
    components: { LMap, LControl, LGeoJson, LTileLayer },
    props: {
      'mapData': { type: Object, required: true }
    },
    data() {
      return {
        basemap: BASEMAP,
        hiddenInstructorIds: {},
        leafletOptions: {
          fullscreenControl: true
        },
        svgPatternsInjected: false,
        geoJSONOptions: {
          onEachFeature: (feature, layer) => {
            if (feature.properties && feature.properties.tooltip) {
              layer.bindTooltip(feature.properties.tooltip, {
                sticky: true,
                className: 'instructor-map-tooltip'
              });
            }
          }
        }
      }
    },
    computed: {
      instructorColors() {
        const colors = {};
        if (!this.mapData || !this.mapData.instructors) return colors;
        this.mapData.instructors.forEach((instructor) => {
          colors[instructor.id] = colorHash.hex(instructor.firstname + ' ' + instructor.lastname);
        });
        return colors;
      },

      sectorInstructorMap() {
        const mapping = {};
        const seen = {};
        if (!this.mapData || !this.mapData.instructors) return mapping;
        this.mapData.instructors.forEach((instructor) => {
          if (!instructor.zoneparts) return;
          instructor.zoneparts.forEach((zp) => {
            if (!mapping[zp.id]) {
              mapping[zp.id] = [];
              seen[zp.id] = {};
            }
            if (seen[zp.id][instructor.id]) return;
            seen[zp.id][instructor.id] = true;
            mapping[zp.id].push({
              id: instructor.id,
              name: instructor.firstname + ' ' + instructor.lastname,
              color: this.instructorColors[instructor.id]
            });
          });
        });
        return mapping;
      },

      stripePatterns() {
        const patterns = {};
        const sectorMap = this.sectorInstructorMap;
        const hidden = this.hiddenInstructorIds;
        Object.keys(sectorMap).forEach((sectorId) => {
          const visibleInstructors = sectorMap[sectorId].filter(i => !hidden[i.id]);
          if (visibleInstructors.length < 2) return;
          const colors = visibleInstructors.map(i => i.color).sort();
          const colorKey = colors.join('-');
          if (patterns[colorKey]) return;
          patterns[colorKey] = {
            id: 'stripe-' + colorKey.replace(/#/g, ''),
            colors: colors
          };
        });
        return patterns;
      },

      processedLayers() {
        if (!this.mapData || !this.mapData.geojson) return [];
        const sectorMap = this.sectorInstructorMap;
        const hidden = this.hiddenInstructorIds;

        return this.mapData.geojson
          .filter((feature) => feature.geometry)
          .map((feature) => {
            const sectorId = feature.properties.sector_id;
            const allInstructors = sectorMap[sectorId] || [];
            const visibleInstructors = allInstructors.filter(i => !hidden[i.id]);
            const sectorName = feature.properties.name;

            let tooltipLines = [sectorName];
            let fillColor;
            let fillOpacity;
            let borderColor;

            if (allInstructors.length === 0) {
              if (hidden['unassigned']) return null;
              fillColor = UNASSIGNED_COLOR;
              fillOpacity = 0.2;
              borderColor = UNASSIGNED_COLOR;
              tooltipLines.push('Unassigned');
            } else if (visibleInstructors.length === 0) {
              fillColor = UNASSIGNED_COLOR;
              fillOpacity = 0.15;
              borderColor = UNASSIGNED_COLOR;
            } else if (visibleInstructors.length === 1) {
              fillColor = visibleInstructors[0].color;
              fillOpacity = 0.4;
              borderColor = visibleInstructors[0].color;
              tooltipLines.push(visibleInstructors[0].name);
            } else {
              const colorKey = visibleInstructors.map(i => i.color).sort().join('-');
              const pattern = this.stripePatterns[colorKey];
              fillColor = pattern ? 'url(#' + pattern.id + ')' : visibleInstructors[0].color;
              fillOpacity = 0.5;
              borderColor = visibleInstructors[0].color;
              tooltipLines.push('Instructors:');
              visibleInstructors.forEach(i => tooltipLines.push('  ' + i.name));
            }

            const geojson = {
              type: 'Feature',
              geometry: feature.geometry,
              properties: {
                name: sectorName,
                tooltip: tooltipLines.join('<br>')
              }
            };

            const style = {
              fillColor: fillColor,
              weight: 2,
              opacity: 1,
              color: borderColor,
              dashArray: '3',
              fillOpacity: fillOpacity
            };

            return { geojson, style };
          })
          .filter((layer) => layer !== null);
      },

      hasLayers() {
        return this.processedLayers.length > 0;
      },

      legendEntries() {
        const entries = [];
        if (!this.mapData || !this.mapData.instructors) return entries;

        this.mapData.instructors.forEach((instructor) => {
          entries.push({
            id: instructor.id,
            name: instructor.firstname + ' ' + instructor.lastname,
            color: this.instructorColors[instructor.id]
          });
        });

        const sectorMap = this.sectorInstructorMap;
        const hasUnassigned = this.mapData.geojson.some((feature) => {
          return feature.geometry && !sectorMap[feature.properties.sector_id];
        });
        if (hasUnassigned) {
          entries.push({
            id: 'unassigned',
            name: 'Unassigned',
            color: UNASSIGNED_COLOR
          });
        }

        return entries;
      },

      bounds() {
        const latlngs = [];
        if (!this.mapData || !this.mapData.geojson) return null;

        this.mapData.geojson.forEach((feature) => {
          if (!feature.geometry || !feature.geometry.coordinates) return;
          feature.geometry.coordinates.forEach((coords) => {
            coords.forEach((ring) => {
              if (ring.length === 2 && typeof ring[0] === 'number') {
                latlngs.push(L.GeoJSON.coordsToLatLng(ring));
              } else {
                ring.forEach((point) => {
                  latlngs.push(L.GeoJSON.coordsToLatLng(point));
                });
              }
            });
          });
        });

        return latlngs.length > 1 ? latLngBounds(latlngs) : null;
      },

      center() {
        if (this.mapData && this.mapData.center) {
          return [this.mapData.center.lat, this.mapData.center.lng];
        }
        return [0, 0];
      }
    },
    methods: {
      toggleInstructor(entry) {
        if (this.hiddenInstructorIds[entry.id]) {
          this.$delete(this.hiddenInstructorIds, entry.id);
        } else {
          this.$set(this.hiddenInstructorIds, entry.id, true);
        }
      },

      isHidden(entry) {
        return !!this.hiddenInstructorIds[entry.id];
      },

      legendSwatchStyle(entry) {
        return 'background-color: ' + entry.color;
      },

      injectSvgPatterns() {
        if (this.svgPatternsInjected) return;
        const patterns = this.stripePatterns;
        const patternKeys = Object.keys(patterns);
        if (!patternKeys.length) return;

        this.$nextTick(() => {
          const mapEl = this.$refs.serviceAreaMap;
          if (!mapEl || !mapEl.mapObject) return;
          const container = mapEl.mapObject.getContainer();
          const svg = container.querySelector('svg');
          if (!svg) return;

          let defs = svg.querySelector('defs');
          if (!defs) {
            defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
            svg.insertBefore(defs, svg.firstChild);
          }

          patternKeys.forEach((key) => {
            const pattern = patterns[key];
            if (defs.querySelector('#' + pattern.id)) return;

            const patternEl = document.createElementNS('http://www.w3.org/2000/svg', 'pattern');
            patternEl.setAttribute('id', pattern.id);
            patternEl.setAttribute('width', STRIPE_WIDTH);
            patternEl.setAttribute('height', STRIPE_WIDTH);
            patternEl.setAttribute('patternUnits', 'userSpaceOnUse');
            patternEl.setAttribute('patternTransform', 'rotate(45)');

            const stripeWidth = STRIPE_WIDTH / pattern.colors.length;
            pattern.colors.forEach((color, i) => {
              const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
              rect.setAttribute('x', stripeWidth * i);
              rect.setAttribute('y', 0);
              rect.setAttribute('width', stripeWidth);
              rect.setAttribute('height', STRIPE_WIDTH);
              rect.setAttribute('fill', color);
              patternEl.appendChild(rect);
            });

            defs.appendChild(patternEl);
          });

          this.svgPatternsInjected = true;
        });
      }
    },
    watch: {
      stripePatterns: {
        handler() {
          this.svgPatternsInjected = false;
          this.$nextTick(() => this.injectSvgPatterns());
        },
        deep: true
      }
    },
    mounted() {
      this.$nextTick(() => {
        this.injectSvgPatterns();
      });
    },
    beforeDestroy() {
      if (this.$refs.serviceAreaMap && this.$refs.serviceAreaMap.mapObject) {
        this.$refs.serviceAreaMap.mapObject.off();
      }
    }
  }
</script>

<style scoped>
  .instructor-legend {
    max-height: 400px;
    overflow-y: auto;
    min-width: 180px;
  }
  .instructor-legend strong {
    display: block;
    margin-bottom: 6px;
    font-size: 14px;
  }
  .map-info {
    padding: 8px 10px;
    font: 14px/18px Arial, Helvetica, sans-serif;
    background: rgba(255, 255, 255, 0.9);
    box-shadow: 0 0 15px rgba(0, 0, 0, 0.2);
    border-radius: 5px;
  }
  .legend-entry {
    display: flex;
    align-items: center;
    padding: 3px 0;
    cursor: pointer;
    margin: 0;
    font-weight: normal;
  }
  .legend-entry:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }
  .legend-entry input[type="checkbox"] {
    margin: 0 6px 0 0;
    cursor: pointer;
  }
  .legend-entry span {
    flex: 1;
  }
  .legend-entry--hidden span {
    opacity: 0.4;
    text-decoration: line-through;
  }
  .legend-entry--hidden .zone-theme {
    opacity: 0.2;
  }
  .zone-theme {
    width: 18px;
    height: 18px;
    min-width: 18px;
    margin-right: 6px;
    opacity: 0.7;
    display: inline-block;
  }
</style>

<style>
  .instructor-map-tooltip {
    font-size: 14px;
    line-height: 1.4;
    padding: 8px 12px;
  }
</style>
